import { ReactComponent as DefaultAvatarIcon } from '@assets/icons/V4/default-profile-pic.svg';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import SkeletonBox from '@components/V4/SkeletonBox';
import { CheckCircleIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { WhatsAppGroupDTO } from '@shared/clientTypes/DTOs/UserDTO';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useEffect, useState } from 'react';

const REMINDER_INTERVAL_DAYS = 7;

export const shouldRemind = (groups: WhatsAppGroupDTO[], dismissedAt?: string | null) => {
  if (!groups.some((group) => !group.joined)) return false;
  if (!dismissedAt) return true;

  return moment().diff(moment(dismissedAt), 'days') >= REMINDER_INTERVAL_DAYS;
};

interface WhatsAppGroupsModalProps {
  title: string;
  description: string;
  groups: WhatsAppGroupDTO[];
  dismissedAt?: string | null;
  isLoading: boolean;
  isSaving: boolean;
  showGroupNames?: boolean;
  onToggleJoined: (studentId: string, joined: boolean) => void;
  onDismiss: () => void;
}

const GroupRow = (props: {
  group: WhatsAppGroupDTO;
  showName: boolean;
  isSaving: boolean;
  onToggleJoined: (studentId: string, joined: boolean) => void;
}) => {
  const { group, showName, isSaving, onToggleJoined } = props;

  return (
    <li
      className={mergeClassNames(
        'flex items-center gap-4 rounded-xl border border-white/10 bg-surfaceObject p-4 transition-colors',
        group.joined && 'border-customGreen/40 bg-customGreen/5'
      )}
    >
      {showName &&
        (group.avatar ? (
          <img
            src={group.avatar}
            crossOrigin="anonymous"
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <DefaultAvatarIcon className="h-10 w-10 shrink-0 rounded-full" />
        ))}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {showName && (
          <span className="truncate font-raleway text-sm font-bold text-white">
            {group.fullName}
          </span>
        )}
        <a
          href={group.whatsappGroupLink}
          target="_blank"
          rel="noreferrer"
          className="flex w-fit items-center gap-1.5 font-raleway text-xs font-semibold text-customPrimary hover:underline"
        >
          Open WhatsApp group
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </a>
      </div>

      <button
        type="button"
        disabled={isSaving}
        onClick={() => onToggleJoined(group.studentId, !group.joined)}
        aria-pressed={group.joined}
        className={mergeClassNames(
          'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 font-raleway text-xs font-semibold transition-colors disabled:opacity-50',
          group.joined
            ? 'border-customGreen/50 bg-customGreen/10 text-customGreen'
            : 'border-white/20 text-customGrey hover:border-customPrimary hover:text-customPrimary'
        )}
      >
        <CheckCircleIcon className="h-4 w-4" />
        {group.joined ? 'Joined' : "I'm in"}
      </button>
    </li>
  );
};

const WhatsAppGroupsModal = (props: WhatsAppGroupsModalProps) => {
  const {
    title,
    description,
    groups,
    dismissedAt,
    isLoading,
    isSaving,
    showGroupNames = true,
    onToggleJoined,
    onDismiss,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [hasDecided, setHasDecided] = useState(false);

  useEffect(() => {
    if (isLoading || hasDecided) return;

    setIsOpen(shouldRemind(groups, dismissedAt));
    setHasDecided(true);
  }, [groups, dismissedAt, isLoading, hasDecided]);

  const pendingCount = groups.filter((group) => !group.joined).length;
  const isDone = pendingCount === 0;

  const closeHandler = () => {
    setIsOpen(false);
    onDismiss();
  };

  return (
    <Modal isOpen={isOpen} onClose={closeHandler} size="small">
      <Modal.Header title={title} className="text-center" />
      <Modal.Body className="mb-8 mt-2 flex flex-col gap-6">
        <p className="text-center font-raleway text-xs leading-5 text-customGrey">{description}</p>

        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[...Array(2).keys()].map((index) => (
              <SkeletonBox key={index} className="h-[74px] w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <ul className="flex max-h-[45vh] flex-col gap-3 overflow-y-auto">
            {groups.map((group) => (
              <GroupRow
                key={group.studentId}
                group={group}
                showName={showGroupNames}
                isSaving={isSaving}
                onToggleJoined={onToggleJoined}
              />
            ))}
          </ul>
        )}

        {!isLoading && (
          <p
            className={mergeClassNames(
              'text-center font-raleway text-xs font-semibold',
              isDone ? 'text-customGreen' : 'text-customGold'
            )}
          >
            {isDone
              ? 'You are in every group. Thank you!'
              : `${pendingCount} ${pendingCount === 1 ? 'group' : 'groups'} left to join`}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-center gap-4">
        <Button
          variant={isDone ? 'primary' : 'secondary'}
          buttonText={isDone ? 'Done' : 'Remind me next week'}
          onClick={closeHandler}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default WhatsAppGroupsModal;
